import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { UserRole } from '@cls/shared';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';

interface WorkerRef { id: string; name: string; email: string | null; role: string | null }

interface ParsedRow {
  email: string;
  name: string;
  role: UserRole;
  roleExplicit: boolean;
  workerId: string | null;
  error: string | null;
}

interface InviteResult {
  email: string;
  name: string;
  status: 'invited' | 'exists' | 'error';
  reason?: string;
  emailSent: boolean;
  inviteLink: string | null;
}

const ROLE_WORDS: Record<string, UserRole> = {
  admin: UserRole.Admin,
  manager: UserRole.Manager,
  supervisor: UserRole.Supervisor,
  worker: UserRole.Worker,
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function parseLines(text: string, defaultRole: UserRole, workers: WorkerRef[]): ParsedRow[] {
  const workersByEmail = new Map(workers.filter((w) => w.email).map((w) => [w.email!.toLowerCase(), w]));
  const rows: ParsedRow[] = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const cells = line.split(/[\t;,]/).map((c) => c.trim()).filter(Boolean);
    const email = (cells.find((c) => c.includes('@')) ?? '').toLowerCase();
    const roleCell = cells.find((c) => ROLE_WORDS[c.toLowerCase()]);
    const role = roleCell ? ROLE_WORDS[roleCell.toLowerCase()]! : defaultRole;
    const nameCells = cells.filter((c) => c !== email && c !== roleCell && !c.includes('@'));
    let name = nameCells.join(' ').trim();
    if (!name && email) {
      name = email.split('@')[0]!.replace(/[._-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
    }
    let error: string | null = null;
    let workerId: string | null = null;
    if (!EMAIL_RE.test(email)) error = 'No valid email on this line';
    else if (!name) error = 'Missing name';
    if (!error && role === UserRole.Worker) {
      const match = workersByEmail.get(email);
      if (match) workerId = match.id;
      else error = 'No worker record with this email — add the worker first or pick another role';
    }
    rows.push({ email, name, role, roleExplicit: !!roleCell, workerId, error });
  }
  const seen = new Set<string>();
  for (const r of rows) {
    if (r.error) continue;
    if (seen.has(r.email)) r.error = 'Duplicate email';
    else seen.add(r.email);
  }
  return rows;
}

export function InviteUsersModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [defaultRole, setDefaultRole] = useState<UserRole>(UserRole.Manager);
  const [results, setResults] = useState<InviteResult[] | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: workers = [] } = useQuery({
    queryKey: ['workers'],
    queryFn: () => api.get<WorkerRef[]>('/workers').then((r) => r.data),
  });

  const parsed = useMemo(() => parseLines(text, defaultRole, workers), [text, defaultRole, workers]);
  const valid = parsed.filter((r) => !r.error);
  const invalid = parsed.filter((r) => r.error);

  const send = useMutation({
    mutationFn: () => api.post<InviteResult[]>('/users/invite', {
      invites: valid.map((r) => ({ email: r.email, name: r.name, role: r.role, workerId: r.workerId })),
    }).then((r) => r.data),
    onSuccess: (data) => {
      setResults(data);
      qc.invalidateQueries({ queryKey: ['users'] });
      const ok = data.filter((r) => r.status === 'invited').length;
      toast.success(`${ok} invite${ok !== 1 ? 's' : ''} created`, data.some((r) => r.status === 'invited' && !r.emailSent) ? 'Some emails could not be sent — copy the links below' : undefined);
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error('Invite failed', e?.response?.data?.message ?? 'Check the rows and try again'),
  });

  const copyLink = async (link: string) => {
    await navigator.clipboard.writeText(link);
    toast.success('Invite link copied');
  };

  const statusChip = (r: InviteResult) => {
    if (r.status === 'invited' && r.emailSent) return <span className="badge b-valid">✓ Invited · email sent</span>;
    if (r.status === 'invited') return <span className="badge b-expiring">✓ Invited · email failed</span>;
    if (r.status === 'exists') return <span className="badge b-tag">Already exists</span>;
    return <span className="badge b-expired">✗ {r.reason ?? 'Failed'}</span>;
  };

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 640, maxWidth: '95vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">✉ Invite Users</div>
        <div className="modal-sub">
          Each user gets an email with a link to set their own password — no more typing passwords for people.
        </div>

        {results ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 360, overflowY: 'auto', marginTop: 10 }}>
              {results.map((r) => (
                <div key={r.email} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{r.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.email}</div>
                  </div>
                  {statusChip(r)}
                  {r.inviteLink ? (
                    <button className="btn btn-ghost btn-xs" onClick={() => copyLink(r.inviteLink!)} title="Copy the set-password link — send it by WhatsApp if the email didn't arrive">⧉ Copy link</button>
                  ) : null}
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => { setResults(null); setText(''); }}>Invite more</button>
              <button className="btn btn-primary" onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <>
            <div className="form-row" style={{ marginTop: 10 }}>
              <label className="form-label">People to invite — one per line, pasted straight from Excel</label>
              <textarea
                className="form-input"
                style={{ minHeight: 120, fontFamily: 'monospace', fontSize: 11, resize: 'vertical' }}
                placeholder={'graham@cls.co.uk\tGraham Smith\tAdmin\nsite.manager@cls.co.uk\tJo Bloggs\nworker@cls.co.uk\tSam Jones\tWorker'}
                value={text}
                onChange={(e) => setText(e.target.value)}
                autoFocus
              />
              <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 3 }}>
                Columns can be separated by tabs, commas or semicolons: email, name, role (optional). Missing names are guessed from the email.
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">Role for lines without one</label>
              <select className="form-select" value={defaultRole} onChange={(e) => setDefaultRole(e.target.value as UserRole)} style={{ maxWidth: 260 }}>
                <option value={UserRole.Manager}>Manager — Read-only (+ WA/email)</option>
                <option value={UserRole.Admin}>Admin — Full Access</option>
                <option value={UserRole.Supervisor}>Supervisor — Read-only, own sites (assign sites after)</option>
                <option value={UserRole.Worker}>Worker — Self-service only</option>
              </select>
            </div>

            {parsed.length > 0 ? (
              <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                      <th style={{ padding: '6px 10px' }}>Email</th>
                      <th style={{ padding: '6px 10px' }}>Name</th>
                      <th style={{ padding: '6px 10px' }}>Role</th>
                      <th style={{ padding: '6px 10px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.map((r, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '5px 10px' }}>{r.email || '—'}</td>
                        <td style={{ padding: '5px 10px' }}>{r.name || '—'}</td>
                        <td style={{ padding: '5px 10px' }}>{r.role}{r.roleExplicit ? '' : ' *'}</td>
                        <td style={{ padding: '5px 10px' }}>
                          {r.error
                            ? <span style={{ color: 'var(--danger)', fontSize: 10 }}>✗ {r.error}</span>
                            : <span style={{ color: 'var(--live)', fontSize: 10 }}>✓ ready</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            <div className="modal-actions">
              <span style={{ fontSize: 10, color: 'var(--muted)', marginRight: 'auto', alignSelf: 'center' }}>
                {valid.length} ready{invalid.length ? ` · ${invalid.length} with issues (skipped)` : ''}
              </span>
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={() => send.mutate()} disabled={valid.length === 0 || send.isPending}>
                {send.isPending ? 'Inviting…' : `Send ${valid.length} invite${valid.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
